﻿import { Callback, clamp } from "../utils";
import { InterpolationFunction, lerp } from "../interpolation";
import { vec2 } from "@tlaukkan/tsm";
import { System } from "ecsy";

export type Waypoint = { completePercent: number, cb: Callback<void> }

interface MapObject<T> {
	[key: string]: T;
}

/**Easily animate things over time. Ported in from startust trail */
export class AnimationSystem extends System {
	animations: Array<Timer> = [];
	animationRemoveList: Array<Timer> = [];

	private static instance?: AnimationSystem;

	static getInstance() {
		if (!this.instance) {
			throw new Error('Tried to use animationSystem before it was initialized');
		}
		return this.instance;
	}

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);

		AnimationSystem.instance = this;
	}

	addAnimation(animation: Timer) {
		const removeIndex = this.animationRemoveList.indexOf(animation);
		if (removeIndex !== -1) {
			this.animationRemoveList.splice(removeIndex, 1);
		}
		else {
			this.animations.push(animation);
		}
	}

	removeAnimation(animation: Timer) {
		this.animationRemoveList.push(animation);
	}

	execute(delta: number) {
		this.animations.forEach(anim => anim.update(delta));
		//Removed animations queued for stopping
		this.animationRemoveList.forEach(finishedAnim => {
			//iterate backwards to locate anim because the longer an animation runs the less likely it is being stopped
			for (var i = this.animations.length; i >= 0; --i) {
				if (this.animations[i] === finishedAnim) {
					this.animations.splice(i, 1);
					break;
				}
			}
		});
		this.animationRemoveList = [];
	}
}

export async function animate(targetObject: MapObject<any>,
	property: string, targetValue: number, duration: number, interp: InterpolationFunction = lerp, animList?: Array<PropertyAnimation>) {
	const anim = new PropertyAnimation(targetObject, property, targetValue, duration, interp).start();
	if (animList) animList.push(anim);
	return anim.done;
}

export async function wait(seconds: number) {
	const anim = new Timer(seconds);
	anim.start();
	return anim.done;
}

export async function animateSetter(
	setter: (val: number) => any,
	startValue: number,
	targetValue: number, duration: number,
	interp: InterpolationFunction = lerp) {
	return new SetterAnimation(startValue, setter, targetValue, duration, interp).start().done;
}

export async function animate2D(
	animatedObject: MapObject<any>,
	property: string,
	targetValue: vec2,
	duration: number,
	interp: InterpolationFunction = lerp) {
	return new Property2DAnimation(animatedObject, property, targetValue, duration, interp).start().done;
}

class Timer {
	duration: number;
	done: Promise<this>;
	loop = false;
	isPaused = false;
	rate = 1;

	private completionCallback?: Callback<void>;
	private cancelCallback?: Callback<void>;
	private waypoints = new Array<Waypoint>();

	private fastForwardTarget = NaN;
	private fastForwardRate = 1;

	protected progress = 0;

	constructor(duration: number) {
		this.duration = duration;
		this.done = new Promise((resolve, reject) => {
			this.completionCallback = () => {
				resolve(this);
			};
			if (reject) this.cancelCallback = reject;
		});
	}

	update(delta: number) {
		const progressThisTick = (delta * this.rate * this.fastForwardRate) / this.duration;
		this.progress = Math.min(this.progress + progressThisTick, 1);

		if (this.fastForwardTarget >= this.progress) {
			this.fastForwardRate = 1;
			this.fastForwardTarget = NaN;
		}

		if (this.progress == 1) {
			this.onEnd();
		}
	}

	protected checkAndTriggerWaypoints() {
		this.waypoints.forEach(waypoint => {
			if (this.progress > waypoint.completePercent) {
				waypoint.cb();
				waypoint.completePercent = 999; //will never trigger
			}
		});
	}

	protected onEnd() {
		if (this.loop)
			this.progress = 0;
		else
			this.stop();
	}

	/** Start the animation, from property's current value to duration */
	start(loop = false) {
		this.loop = loop;
		AnimationSystem.getInstance().addAnimation(this);
		return this;
	}

	/** Stop, reset all progress, and issue end callback*/
	stop() {
		if (this.completionCallback) {
			this.completionCallback();
		}
		return this.cancel();
	}

	/** Stop, reset all progress, and do NOT issue end callback*/
	cancel() {
		AnimationSystem.getInstance().removeAnimation(this);
		this.progress = 0;
		if (this.cancelCallback) {
			this.cancelCallback();
		}
		return this;
	}

	/** Pause in progress, progress saved, can resume later */
	pause() {
		AnimationSystem.getInstance().removeAnimation(this);
		this.isPaused = true;
		return this;
	}

	/** Resume an animation that has been paused */
	resume() {
		AnimationSystem.getInstance().addAnimation(this);
		this.isPaused = false;
		return this;
	}

	getProgress() {
		return this.progress;
	}

	jumpTo(progress: number) {
		this.progress = progress;
	}

	fastForwardTo(target: number, rate: number) {
		this.fastForwardTarget = target;
		this.fastForwardRate = rate;
	}

	async addWaypoint(percent: number) {
		return new Promise<Timer>((resolve, reject) => {
			this.waypoints.push({
				completePercent: percent, cb: () => {
					resolve(this);
				}
			});
		})
	}

}

export abstract class BasicAnimation extends Timer {

	protected interpolatonFunction: InterpolationFunction
	private forward = true;

	constructor(duration: number, interp: InterpolationFunction = lerp) {
		super(duration);
		this.interpolatonFunction = interp;
	}

	/** Updates the animated value by applying the interpolation function and current progress */
	abstract updateValue(): void;

	/**Records the starting value of the animation*/
	abstract setStartValue(): void;

	start(loop = false) {
		this.setStartValue();
		return super.start(loop);
	}

	reverse() {
		this.forward = false;
		this.start();
	}

	update(delta: number) {
		const progressThisTick = (delta * this.rate) / this.duration;
		const direction = this.forward ? 1 : -1;
		this.progress = this.progress + (progressThisTick * direction);
		this.progress = clamp(this.progress, 0, 1);

		this.updateValue();
		this.checkAndTriggerWaypoints();

		if (this.progress == (this.forward ? 1 : 0)) {
			this.onEnd();
		}
	}
}

export class PropertyAnimation extends BasicAnimation {
	animatedObject: MapObject<any>;
	property: string;
	targetValue: number;
	startValue!: number;

	constructor(targetObject: MapObject<any>, property: string,
		targetValue: number, duration: number,
		interp: InterpolationFunction = lerp) {
		super(duration, interp);
		this.animatedObject = targetObject;
		this.property = property;
		this.targetValue = targetValue;
	}

	updateValue() {
		var currentValue = this.interpolatonFunction(this.startValue, this.targetValue, this.progress);
		this.animatedObject[this.property] = currentValue;
	}

	setStartValue() {
		this.startValue = this.animatedObject[this.property];
	}

}

export class SetterAnimation extends BasicAnimation {

	constructor(
		public startValue: number,
		private setter: (val: number) => any,
		public targetValue: number, duration: number,
		interp: InterpolationFunction = lerp) {
		super(duration, interp);
		this.targetValue = targetValue;
	}

	updateValue() {
		var currentValue = this.interpolatonFunction(this.startValue, this.targetValue, this.progress);
		this.setter(currentValue);
	}

	setStartValue() {
		//We assume for these that the passed in startValue is correct, and has not changed since
	}
}

class Property2DAnimation extends BasicAnimation {
	startValue!: vec2;

	constructor(private animatedObject: MapObject<any>
		, public property: string
		, public targetValue: vec2, duration: number
		, interp: InterpolationFunction = lerp) {
		super(duration, interp);
	}

	updateValue() {
		var x = this.interpolatonFunction(this.startValue.x, this.targetValue.x, this.progress);
		var y = this.interpolatonFunction(this.startValue.y, this.targetValue.y, this.progress);
		this.animatedObject[this.property] = new vec2([x, y]);
	}

	setStartValue() {
		this.startValue = (this.animatedObject[this.property] as vec2).copy();
	}
}